import { Container } from "@mui/material";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import AdView from "./components/ui/AdView";
import DialogMessage from "./components/ui/DialogMessage";
import GnbSearch from "./components/ui/GnbSearch";
import LoadingBar from "./components/ui/LoadingBar";
import RiaBottom from "./components/ui/RiaBottom";
import ToastMessage from "./components/ui/ToastMessage";
import useMaxlengthPolyfill from "./hooks/useMaxlengthPolyfill";
import BoardInfo from "./pages/BoardInfo";
import ZoomableImageModal from "./pages/BoardInfo/ZoomableImageModal";
import BoardSearchList from "./pages/BoardSearchList";
import CommunityBoardList from "./pages/CommunityBoardList";
import ImageMaker from "./pages/ImageMaker";
import { Ad_RefreshKey, CommunityBoardListState_BoardSeq, CommunityBoardListState_SubMenu, ImgViewer, IsShowDetail, IsShowReg, MoveButtonState, ToastMessageState } from "./recoil/states";
import { asyncReqCommunityBoardDetail } from "./reducers/CommunityBoardDetailReducer";
import { asyncReqCommunityBoardList, CommunityBoardListActions } from "./reducers/CommunityBoardListReducer";
import { asyncReqCommunityReplyList, CommunityReplyListActions } from "./reducers/CommunityReplyListReducer";
import { asyncReqIsLogin, LoginBtnsActions } from "./reducers/LoginBtnsReducer";
import { TopMainBtnsActions } from "./reducers/TopMainBtnsReducer";
import { AppDispatch } from "./Store";
import CommonUtil from './utils/CommonUtil';

const App = () => {

  const [ad_RefreshKey, setAd_RefreshKey] = useRecoilState(Ad_RefreshKey); // 광고 UI 랜더링 처리를 위해 사용되는 state
  const setToastMessageState = useSetRecoilState(ToastMessageState); // 토스트 메세지 요청 정보를 갖는 state
  const [imgViewer, setImgViewer] = useRecoilState(ImgViewer); // 이미지 뷰어 UI 랜더링 처리를 위해 사용되는 state
  const setCommunityBoardListState_BoardSeq = useSetRecoilState(CommunityBoardListState_BoardSeq); // 커뮤니티 게시판 상세 UI 랜더링 처리를 위해 사용되는 state
  const setCommunityBoardListState_SubMenu = useSetRecoilState(CommunityBoardListState_SubMenu); // 커뮤니티 게시판 상단버튼 UI 랜더링 처리를 위해 사용되는 state
  const setIsShowReg = useSetRecoilState(IsShowReg); // 등록 UI 표시 여부
  const setIsShowDetail = useSetRecoilState(IsShowDetail); // 상세 UI 표시 여부
  const moveButtonState = useRecoilValue(MoveButtonState); // 하단 이동 버튼 상태 처리를 위해 사용되는 state

  const navigate = useNavigate();

  (window as any).urlChange = (url: string, opts?: { hard?: boolean }) => {

    // 일정시간 이후에 한 번씩 새로고침 처리 (메모리 문제로 성능 문제가 발생해서 조치된 사항 - 어느 시점부터 애드센스 광고도 나오지 않음..)
    try {
      const HARD_MS = Number(process.env.REACT_APP_REFRESH_MILLISECOND || 0);
      const KEY_TS = "__app_last_full_refresh_ts__";
      const KEY_CNT = "__app_nav_count__";

      const now = Date.now();
      const last = Number(sessionStorage.getItem(KEY_TS) || "0");
      let cnt = Number(sessionStorage.getItem(KEY_CNT) || "0") + 1;
      sessionStorage.setItem(KEY_CNT, String(cnt));

      // 하드 리로드 조건
      const timeOk = HARD_MS > 0 && last && (now - last >= HARD_MS);
      const manyNav = cnt >= 50; // 내비게이션 누적이 많을 때도 한 번 청소
      const memHigh =
        (performance as any).memory &&
        (performance as any).memory.usedJSHeapSize > 700 * 1024 * 1024;

      const hard = !!(opts?.hard || timeOk || manyNav || memHigh);

      if (hard) {
        sessionStorage.setItem(KEY_TS, String(now));
        sessionStorage.setItem(KEY_CNT, "0");
        // 히스토리 추가를 원하지 않으면 replace 사용
        window.location.assign(url);
        return;
      }
    } catch { }

    // 동일 틱의 다른 작업(비디오 pause 등)과 충돌 최소화
    setTimeout(() => navigate(url), 0);
  };

  (window as any).showToastMessage = (message: string) => {
    setToastMessageState(rawState => ({ ...rawState, isShow: true, message }));
  }

  if (!CommonUtil.isMobileOs()) document.body.classList.add('is-pc'); // PC일 경우, 이동버튼 hover 효과 적용

  // ===============================================================================================
  // 새로고침 직전에 이벤트를 잡기위한 로직 - 시작

  // 최신 moveButtonState 값을 읽기 위한 ref
  const lastMoveIsOrderRef = useRef(moveButtonState.isClickBtnOrder);
  useEffect(() => {
    lastMoveIsOrderRef.current = moveButtonState.isClickBtnOrder;
  }, [moveButtonState.isClickBtnOrder]);

  useEffect(() => {
    const onExit = (reason: string) => {
      try {
        sessionStorage.setItem(
          "__last_move_btn_type__",
          lastMoveIsOrderRef.current ? "order" : "random"
        );
      } catch { }
      // 서버 로그 전송 로직이 있다면 여기에서 sendBeacon/keepalive 등 사용
    };

    const onVis = () => { if (document.visibilityState === "hidden") onExit("visibilitychange"); };
    const onHide = (e: PageTransitionEvent) => { onExit(`pagehide:${e.persisted ? "bfcache" : "normal"}`); };
    const onBefore = () => { onExit("beforeunload"); };

    document.addEventListener("visibilitychange", onVis, { capture: true });
    window.addEventListener("pagehide", onHide as any, { capture: true });
    window.addEventListener("beforeunload", onBefore);

    return () => {
      document.removeEventListener("visibilitychange", onVis, { capture: true } as any);
      window.removeEventListener("pagehide", onHide as any, { capture: true } as any);
      window.removeEventListener("beforeunload", onBefore);
    };
  }, []);

  // 새로고침 직전에 이벤트를 잡기위한 로직 - 종료
  // ===============================================================================================

  // URL 변경 감지되면 진입되는 로직
  const location = useLocation();
  useEffect(() => {
    // StrictMode 초기 2연속 실행 방지: 같은 routeKey에서 1.2초 이내 중복 실행 차단
    const routeKey = location.key || (location.pathname + location.search);
    const DEDUPE_KEY = "__app_ad_dedupe__";
    try {
      const prev = JSON.parse(sessionStorage.getItem(DEDUPE_KEY) || "null");
      if (prev && prev.routeKey === routeKey && Date.now() - prev.ts < 1200) {
        return; // 중복 실행 스킵
      }
      sessionStorage.setItem(DEDUPE_KEY, JSON.stringify({ routeKey, ts: Date.now() }));
    } catch { /* noop */ }

    dispatch(asyncReqIsLogin({ logout_req_yn: "N", show_dim: false })); // 로그인 여부 값 가져오기 (로딩바 표시되지 않도록 'show_dim' 값을 'false'로 세팅)

    if (location.pathname === "/community_board_list") {

      clearInterval((window as any).interval_comment_roll_id); // 최신댓글 목록 롤링 interval 중지

      const req = CommonUtil.getQuerystring("req", "");
      const board_seq = CommonUtil.getQuerystring("board_seq", "");
      setCommunityBoardListState_BoardSeq(board_seq);

      setIsShowReg(false); // 등록 UI 숨김 요청
      setIsShowDetail(false); // 상세 UI 숨김 요청

      if (req === "R") {
        // 게시물 등록 요청

        setIsShowReg(true); // 등록 UI 표시 요청
      }
      else if (board_seq !== "") {
        // 상세 표시 요청

        setIsShowDetail(true); // 상세 UI 표시 요청

        // 게시물 상세 가져오기
        dispatch(asyncReqCommunityBoardDetail({ board_seq }));

        const page_size = parseInt(process.env.REACT_APP_COMMUNITY_REPLY_PAGE_SIZE as string, 10);
        const curr_page = 1;
        dispatch(asyncReqCommunityReplyList({ page_size, curr_page, board_seq, show_dim: false })); // 댓글 목록 가져오기 (최초 진입일 경우에는 로딩바 표시되지 않도록 'show_dim' 값을 'false'로 세팅)
        dispatch(CommunityReplyListActions.setCurrPage(curr_page)); // 현재 페이지 번호 저장
      }
      else {
        // 목록 표시 요청

        // 게시물 목록 가져오기
        const page_size = parseInt(process.env.REACT_APP_COMMUNITY_BOARD_PAGE_SIZE as string, 10);
        const curr_page = parseInt(CommonUtil.getQuerystring("curr_page", "1"), 10);
        const search_type = CommonUtil.getQuerystring("search_type", "");
        const search_keyword = CommonUtil.getQuerystring("search_keyword", "");
        const sub_menu = CommonUtil.getQuerystring("sub_menu", "");
        dispatch(asyncReqCommunityBoardList({ page_size, curr_page, search_type, search_keyword, sub_menu }));
        dispatch(CommunityBoardListActions.setCurrPage(curr_page)); // 현재 페이지 번호 저장

        // 상단버튼 롤오버 갱신
        setCommunityBoardListState_SubMenu(sub_menu);
      }
    }

    // 간혹 동영상 풀스크린모드로 플레이 중 라우트 이동되면, 스크롤락 처리했던게 해제되지 않아서 라우트 변경되면 무조건 스크롤락 해제 처리!
    (window as any).ScrollLocker?.unlock();

    // [URL 변화 + x초 이상 경과] 된 경우, 광고 컴포넌트의 key로 할당되는 ad_RefreshKey 값을 변화시켜서 새로운 광고로 재마운트 유도
    const newRefreshKey = String(Math.floor(Date.now() / parseInt(process.env.REACT_APP_AD_REMOUNT_MILLISECOND as string, 10))); // x초마다 0,1,2,... 증가
    setAd_RefreshKey(`_${newRefreshKey}`);

    // 토스트 메세지가 표시되면 3초 후에 state 값을 false로 바꿔서 사라지게 되는데, 3초가 지나기 전에 라우트 이동되면 다시 토스트 메세지가 표시되는 버그 있음..
    // 해당 문제 해결을 위해 라우트 이동되면 state 값을 false로 강제 변경!
    setToastMessageState(rawState => ({ ...rawState, isShow: false, message: "" }));

    if (navigator.userAgent.indexOf(";UGGIYO_APP;") > -1) {
      // 앱 내 호출일 경우
      // 앱 내 배너광고 reload 처리
      const AppWebView = (window as any).AppWebView;
      if (typeof (AppWebView) !== "undefined") {
        if (typeof (AppWebView.reload_Ad_Banner) === "function") {
          // 전면광고는 랜덤한 타이밍에 표시처리
          const randNum = Math.floor(Math.random() * 10);
          if (randNum === 0) {
            try {
              AppWebView.reload_Ad_Banner("I"); // 전면광고 호출
            } catch (e) { }
          }

          const key = String(Math.floor(Date.now() / parseInt(process.env.REACT_APP_AD_REMOUNT_MILLISECOND as string, 10))); // x초마다 0,1,2,... 증가
          if ((window as any).reload_Ad_Banner_Prev_Key !== key) {
            (window as any).reload_Ad_Banner_Prev_Key = key;
            try {
              AppWebView.reload_Ad_Banner("B"); // 배너광고 호출
            }
            catch (e) {
              AppWebView.reload_Ad_Banner(); // 구버전 앱 사용자는 전면/배너광고 일괄 실행해주는 함수 호출
            }
          }
        }
      }
    }
    else {
      // 웹 브라우저 호출일 경우

      // 메뉴 롤오버 state 기본 배열명 설정 (접속된 페이지명을 기준으로..)
      let defOverArrNm = "";
      let pathname = location.pathname.toLowerCase();
      if (pathname.startsWith("/community_board_list")) defOverArrNm = "COMMUNITY";
      else if (pathname.startsWith("/board_info")) defOverArrNm = "BOARD";
      else if (pathname.startsWith("/image_maker")) defOverArrNm = "IMAGE";
      else defOverArrNm = "BOARD";
      if (defOverArrNm !== "") {
        dispatch(TopMainBtnsActions.setBtnOverClassName({ [defOverArrNm]: "gnb_1da over" }));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useMaxlengthPolyfill(); // 안드로이드 웹뷰에서 입력란에 추가된 maxLength 속성이 먹지 않아서, 해당 문제 해결을 위한 커스텀 훅 호출

  const dispatch = useDispatch<AppDispatch>();

  /**
   * 로그인 팝업 완료 시, Jquery를 활용해서 인위적으로 클릭을 발생시켜 실행되는 이벤트 함수
   * @param e 이벤트 객체
   */
  const handlerLoginOk = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {

    dispatch(LoginBtnsActions.setIsShowLodingBar(false)); // 로딩바 숨김 요청
    dispatch(asyncReqIsLogin({ logout_req_yn: "N" })); // 로그인 여부 값 가져오기
  }

  useEffect(() => {
    // 앱 내에서 호출하는 함수 선언
    (window as any).WebService = (window as any).WebService || {};
    (window as any).WebService.bottom_Button_Call = (callType: string) => {
      // 하단 버튼 클릭 이벤트 실행
      const el = document.querySelector<HTMLAnchorElement>(
        `[data-view-type="${callType}"]`
      );
      el?.click();
    }
    (window as any).WebService.history_Back = (is_confirm: boolean = true) => {
      // 동영상 전체화면 모드일 경우, 전체화면 해제 및 재생중지 처리
      const intercepted = (window as any).videoMinAndPause?.();
      if (intercepted) return; // 하위 로직 skip

      if ((window as any).canGoBack()) {
        (window as any).safeBack(); // 뒤로가기 실행
      }
      else {
        // 뒤로갈 곳 없음
        (window as any).Native.call('closeActivity', { msg: '현재 화면을 닫으시겠습니까?', is_confirm })
          .then((yn: string) => {
            console.log('closeActivity then :', yn)
            clearInterval((window as any).interval_comment_roll_id); // 최신댓글 목록 롤링 interval 중지
          })
          .catch((err: unknown) => console.log('closeActivity catch :', err));
      }
    }
  }, []);

  //----------------------------------------------------------------------
  // 원본 이미지 보기 모달 다이얼로그 관련 로직 - 시작

  const requestCloseModal = () => {
    setImgViewer(v => ({ ...v, open: false }));
  };

  /**
  * 이미지 영역 클릭 이벤트
  * 
  * @param e 이벤트 객체
  * @returns 
  */
  (window as any).handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const isApp = navigator.userAgent.includes(";UGGIYO_APP;");
    const el = e.currentTarget;
    const chosen = el.currentSrc || el.src; // 실제 표시 중인 URL
    const alt = el.alt || "";

    if (isApp) {
      // 앱일 경우
      const AppWebView = (window as any).AppWebView;
      if (typeof AppWebView?.zoom_In_Out_Image === "function") {
        AppWebView.zoom_In_Out_Image(chosen);
        return;
      }
    }
    if (CommonUtil.isMobileOs()) {
      // 모바일 기기일 경우: 현재 표시 URL로 모달 오픈
      setImgViewer({ open: true, src: chosen, alt });
    }
  };

  // 원본 이미지 보기 모달 다이얼로그 관련 로직 - 종료
  //----------------------------------------------------------------------

  return (
    <>
      <Container maxWidth={false} className="root_max_width" sx={{ px: { xs: 0, md: 3 } }}>
        <GnbSearch />
      </Container>
      <Container maxWidth={false} className="root_max_width">
        <button type="button" id="btnLoginOk" onClick={handlerLoginOk} style={{ display: "none" }} />
        <DialogMessage />
        <ToastMessage />
        <LoadingBar />
        <ZoomableImageModal
          open={imgViewer.open}
          src={imgViewer.src}
          alt={imgViewer.alt}
          onClose={requestCloseModal}
          minScale={0.3}
          maxScale={6}
        />
        {
          !CommonUtil.isMobileOs() && <>
            <div className="siderail left">
              <AdView googleAdsense={{ rootid: "adsense_Ad_Web_Root_Side_1", slot: "6080942517", sideYn: "Y" }}
                kakaoAdfit={{ rootid: "kakao_Ad_Web_Root_Side_1", unit: "DAN-15KXHGor4Uebfird", width: 160, height: 600 }}
                refreshKey={ad_RefreshKey}
              />
            </div>
            <div className="siderail right">
              <AdView googleAdsense={{ rootid: "adsense_Ad_Web_Root_Side_2", slot: "8152572086", sideYn: "Y" }}
                kakaoAdfit={{ rootid: "kakao_Ad_Web_Root_Side_2", unit: "DAN-9jxaALaWcFB6B5yf", width: 160, height: 600 }}
                refreshKey={ad_RefreshKey}
              />
            </div>
          </>
        }
        <Routes>
          <Route path="/" element={<BoardInfo />} />
          <Route path="/community_board_list" element={<CommunityBoardList />} />
          <Route path="/board_info" element={<BoardInfo />} />
          <Route path="/board_search_list" element={<BoardSearchList />} />
          <Route path="/image_maker" element={<ImageMaker />} />
        </Routes>
      </Container>
      <Container maxWidth={false} className="root_max_width" sx={{ px: { xs: 0, md: 3 } }}>
        <RiaBottom />
      </Container>
    </>
  );
}

export default App;
